import React, { useMemo } from 'react';
import { Button } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

import RecordBadge from '../common/RecordBadge';
import SortableList, { moveId } from '../common/SortableList';
import { buildActionMenu } from '../common/actionMenu';
import { Chart } from '../charts/LazyCharts';
import { chartPath } from '../reports/ChartRow';
import { ARROW_DOWN, ARROW_LEFT_RIGHT, ARROW_UP } from '../icons';
import useChartContexts from '../../hooks/useChartContexts';
import { CHART_PIN_SIZE } from '../../services/favorites';
import { normalizeId } from '../../utils/ids';
import { RowMenu } from './HomeSections';

/** The canvas of a chart on Home (a phone's is the same: every tile full width there). */
export const HOME_CHART_HEIGHT = 200;

const pinId = (pin) => normalizeId(pin?._id);
/** What a chart pin is called: its chart's name. */
export const chartPinTitle = (pin) => (pin?.chart && typeof pin.chart.name === 'string' && pin.chart.name) || 'Untitled chart';
const sizeOf = (pin) => (pin?.size === CHART_PIN_SIZE.WIDE ? CHART_PIN_SIZE.WIDE : CHART_PIN_SIZE.SMALL);
const editChartPath = (collectionId, chartId) => `${chartPath(collectionId, chartId)}/edit`;

/**
 * A chart pin's ⋯, in the taxonomy (standards §3): Go to — Open chart, Go to
 * its collection; Record — Make wide / Make small (ruled d18), Move up / Move
 * down (reorder mode only, the drag's twin), Unpin from Home.
 */
export function chartPinMenu({ pin, ids, reordering, onReorder, onUnpin, onResize, navigate }) {
	const id = pinId(pin);
	const index = ids.indexOf(id);
	const collectionId = normalizeId(pin.collection_id);
	const chartId = normalizeId(pin.chart?._id || pin.target_id);
	const wide = sizeOf(pin) === CHART_PIN_SIZE.WIDE;
	return buildActionMenu([
		{ key: 'open', group: 'navigate', glyph: 'open', label: 'Open chart', onClick: () => navigate(chartPath(collectionId, chartId)) },
		{
			key: 'collection',
			group: 'navigate',
			glyph: 'collection',
			label: `Go to ${pin.collection?.name || 'collection'}`,
			hidden: !collectionId,
			onClick: () => navigate(`/collections/${collectionId}`),
		},
		{
			key: 'size',
			group: 'record',
			icon: <ARROW_LEFT_RIGHT />,
			label: wide ? 'Make small' : 'Make wide',
			hidden: !onResize,
			onClick: () => onResize(pin, wide ? CHART_PIN_SIZE.SMALL : CHART_PIN_SIZE.WIDE),
		},
		{ key: 'move-up', group: 'record', icon: <ARROW_UP />, label: 'Move up', disabled: index <= 0, hidden: !onReorder || !reordering, onClick: () => onReorder(moveId(ids, id, -1)) },
		{ key: 'move-down', group: 'record', icon: <ARROW_DOWN />, label: 'Move down', disabled: index < 0 || index >= ids.length - 1, hidden: !onReorder || !reordering, onClick: () => onReorder(moveId(ids, id, 1)) },
		{ key: 'unpin', group: 'record', glyph: 'pin', label: 'Unpin from Home', hidden: !onUnpin, onClick: () => onUnpin(pin) },
	]);
}

/**
 * Home's Charts section (charts A7; plan §4.6, §6.6; ruled q16, d18): the
 * person's charts from any collection, in their own order, each small (a
 * third of the row) or wide (two thirds) — full width on a phone. A tile is:
 *
 *   its kicker   the collection it counts, as its badge (a chart pinned in a
 *                sub-collection counts THAT collection's items)
 *   its name     the door to the chart page, in that collection
 *   the chart    through the lazy boundary, with the run's slot from Home's
 *                ONE batch (`results`, by pin row id): every mark a door, a
 *                failure in its own frame, the last answer held dimmed while
 *                it re-runs. A chart the person may no longer open answers the
 *                one "no longer available" slot, never a name.
 *   its ⋯        chartPinMenu
 *
 * Only the first `count` are drawn (and run): "Show N more" draws the next
 * ones (⚑ d13 — Home stays fast). Reorder turns the section into a list of
 * names with grips — every chart, none of them run — and Done puts it back.
 */
export default function PinnedCharts({
	pins,
	results,
	loading = false,
	count,
	onShowMore,
	reordering = false,
	onReorder,
	onUnpin,
	onResize,
	onRetry,
	moreStep = 12,
}) {
	const navigate = useNavigate();
	const list = useMemo(() => (Array.isArray(pins) ? pins : []), [pins]);
	const items = useMemo(() => list.map((pin) => ({ id: pinId(pin), pin })), [list]);
	const ids = useMemo(() => items.map((item) => item.id), [items]);
	const charts = useMemo(() => list.map((pin) => pin.chart).filter(Boolean), [list]);
	const chartContext = useChartContexts(charts);

	if (reordering) {
		return (
			<SortableList
				items={items}
				ariaLabel='Charts on Home'
				className='home-records home-charts-order'
				getLabel={(item) => chartPinTitle(item.pin)}
				onReorder={onReorder}
				reordering
				renderItem={({ pin }, { handle }) => (
					<div className='home-pin home-pin--chart-row'>
						{handle}
						<div className='home-pin__body home-chart__titles'>
							<RecordBadge kind='collection' record={pin.collection} title={pin.collection?.name || 'Collection'} size='xs' />
							<span className='home-chart__name'>{chartPinTitle(pin)}</span>
						</div>
						<RowMenu menu={chartPinMenu({ pin, ids, reordering, onReorder, onUnpin, onResize, navigate })} name={chartPinTitle(pin)} />
					</div>
				)}
			/>
		);
	}

	const shown = typeof count === 'number' ? list.slice(0, count) : list;
	const rest = list.length - shown.length;
	return (
		<>
			<ul className='home-charts' aria-label='Charts on Home'>
				{shown.map((pin) => {
					const id = pinId(pin);
					const collectionId = normalizeId(pin.collection_id);
					const chart = pin.chart || null;
					const title = chartPinTitle(pin);
					const context = chart ? chartContext(chart) || {} : {};
					return (
						<li key={id} className={`home-chart home-chart--${sizeOf(pin)}`} data-pin-id={id}>
							<div className='home-chart__head'>
								<div className='home-chart__titles'>
									<RecordBadge
										kind='collection'
										record={pin.collection}
										title={pin.collection?.name || 'Collection'}
										size='xs'
										to={collectionId ? `/collections/${collectionId}` : undefined}
										className='home-chart__kicker'
									/>
									<h3 className='home-chart__name'>
										<Link to={chartPath(collectionId, normalizeId(chart?._id || pin.target_id))}>{title}</Link>
									</h3>
								</div>
								<RowMenu menu={chartPinMenu({ pin, ids, reordering: false, onReorder, onUnpin, onResize, navigate })} name={title} />
							</div>
							<div className='home-chart__body'>
								<Chart
									chart={chart}
									result={results ? results.get(id) || null : null}
									loading={loading}
									collectionId={collectionId}
									cardName={context.cardName}
									elements={context.elements}
									showTitle={false}
									headingLevel={4}
									height={HOME_CHART_HEIGHT}
									onRetry={onRetry}
									onEdit={chart && chart.canEdit === true ? () => navigate(editChartPath(collectionId, normalizeId(chart._id))) : undefined}
								/>
							</div>
						</li>
					);
				})}
			</ul>
			{rest > 0 && typeof onShowMore === 'function' ? (
				<Button type='link' className='home-charts__more' onClick={onShowMore}>
					{`Show ${Math.min(rest, moreStep)} more`}
				</Button>
			) : null}
		</>
	);
}

/** Exposed for tests: a chart pin's size in the product's words. */
export const chartPinSizeOf = sizeOf;
